import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { RelationPicker } from './ui';
import { RELATION_CONFIGS } from './relationConfigs';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldTextarea, FieldCustom,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';
import {
  API_BASE, pick, unwrap, statusBadge as statusBadgeNode, formatDateTime as formatDate,
  isClosedStatus, isApprovedStatus, isReceivedStatus,
} from './vehicleload/vehicleLoadShared';

// Bu ekran araç yükleme talebinin tam-sayfa detayıdır. Talepler mobile'dan push
// ile gelir → web'de YENİ kayıt yoktur (POST yok). Bu yüzden "Kaydet ve Yeni" /
// "Klonla" KONULMAZ. Başlık location.state.row'dan gelir; yoksa GET /loads/{id}
// ile çekilir. Satırlar /{id}/details ile lazy çekilir.
// PUT /loads/{id} header alanlarını + satırları replace eder (delta down-sync).
//
// DURUM SABİTLERİ vehicleLoadShared'dan gelir (9 durum). Buradaki eski 5 durumluk
// kopya KALDIRILDI: LOADED artık gelmiyor (→ RECEIVED) ve RED artık CANCELLED değil
// REJECTED. Onay/red asıl olarak liste ekranındaki onay panelinde yapılır.

const EMPTY_FORM = { deliveryDate: '', plate: '', sdmName: '', notes: '', status: '' };

export function VehicleLoadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Araç yükleme talepleri mobile push ile gelir → web'de YENİ kayıt yok.
  const isNew = !id || id === 'new';
  const { canAdd, canEdit } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta('Araç Yükleme Detayı', 'Araç yükleme talebi başlık bilgileri ve istenen ürün satırları.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [detailRow, setDetailRow] = useState(null); // onay/red bilgisi için ham satır
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [audit, setAudit] = useState(null);

  // Satır (ürün) alt-grid
  const [lines, setLines] = useState([]);
  const [linesLoading, setLinesLoading] = useState(false);
  const [lineModal, setLineModal] = useState(null); // { key } düzenleme | {} yeni
  const [lineForm, setLineForm] = useState(null); // { productId, productCode, productName, packSize, requestedCases }

  // Aksiyonlar
  const [actionBusy, setActionBusy] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  const fetchLines = useCallback(async (loadId) => {
    if (!loadId) { setLines([]); return; }
    setLinesLoading(true);
    try {
      const res = await fetch(`${API_BASE}/${loadId}/details`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Satırlar alınamadı (${res.status})`);
      const items = pick(unwrap(await res.json()), 'items', 'Items') ?? [];
      setLines((Array.isArray(items) ? items : []).map((l, i) => ({
        key: String(pick(l, 'id', 'Id') || `row-${i}`),
        productId: pick(l, 'productId', 'ProductId') || '',
        productCode: pick(l, 'productCode', 'ProductCode') || '',
        productName: pick(l, 'productName', 'ProductName') || '',
        packSize: pick(l, 'packSize', 'PackSize') ?? 1,
        requestedCases: pick(l, 'requestedCases', 'RequestedCases') ?? 0,
      })));
    } catch (err) {
      showToast('error', err.message || 'Satırlar alınamadı');
      setLines([]);
    } finally {
      setLinesLoading(false);
    }
  }, []);

  // Başlık: listeden gelindiyse location.state.row, doğrudan URL ile gelindiyse GET /loads/{id}.
  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    setLoading(true);
    let row = location.state?.row;
    if (!row) {
      try {
        const res = await fetch(`${API_BASE}/${id}`, { headers: authHeaders() });
        if (res.ok) row = pick(unwrap(await res.json()) || {}, 'header', 'Header');
      } catch { /* sessiz: satırlar yine de yüklenir */ }
    }
    if (row) {
      setDetailRow(row);
      setForm({
        deliveryDate: pick(row, 'deliveryDate', 'DeliveryDate') || '',
        plate: pick(row, 'plate', 'Plate') || '',
        sdmName: pick(row, 'sdmName', 'SdmName') || '',
        notes: pick(row, 'notes', 'Notes') || '',
        status: pick(row, 'status', 'Status') || '',
      });
      setAudit({ createdBy: pick(row, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(row, 'createdDate', 'CreatedDate'), updatedBy: pick(row, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(row, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
    } else {
      showToast('info', 'Talep başlığı alınamadı. Satırlar yükleniyor…');
    }
    await fetchLines(id);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew, fetchLines]);

  useEffect(() => { load(); }, [load]);

  // ── Satır editörü (SubModal) ──
  const openAddLine = () => { setLineForm({ productId: '', productCode: '', productName: '', packSize: 1, requestedCases: 1 }); setLineModal({ key: null }); };
  const openEditLine = (line) => {
    setLineForm({
      productId: line.productId || '', productCode: line.productCode || '', productName: line.productName || '',
      packSize: line.packSize ?? 1, requestedCases: line.requestedCases ?? 0,
    });
    setLineModal({ key: line.key });
  };
  const setLine = (k, v) => setLineForm((c) => ({ ...c, [k]: v }));

  const confirmLine = () => {
    if (!lineForm?.productName || !String(lineForm.productName).trim()) { showToast('error', 'Bir ürün seçiniz'); return; }
    const next = {
      productId: lineForm.productId || '', productCode: lineForm.productCode || '', productName: lineForm.productName.trim(),
      packSize: Number(lineForm.packSize) || 1, requestedCases: Number(lineForm.requestedCases) || 0,
    };
    if (lineModal.key) {
      setLines((cur) => cur.map((l) => (l.key === lineModal.key ? { ...l, ...next } : l)));
    } else {
      setLines((cur) => [...cur, { key: `new-${cur.length}-${Date.now()}`, ...next }]);
    }
    setLineModal(null);
    setLineForm(null);
  };
  const removeLine = (key) => setLines((cur) => cur.filter((l) => l.key !== key));

  // PUT /loads/{id}: satırlar full-replace edilir (deletedDetailIds YOK).
  const persist = async () => {
    if (isNew) return;
    setSaving(true);
    try {
      const validLines = lines.filter((l) => l.productName && String(l.productName).trim());
      const payload = {
        deliveryDate: form.deliveryDate || null,
        plate: form.plate || null,
        sdmName: form.sdmName || null,
        notes: form.notes || null,
        lines: validLines.map((l) => ({
          productId: l.productId || null,
          productCode: l.productCode || null,
          productName: l.productName,
          packSize: Number(l.packSize) || 1,
          requestedCases: Number(l.requestedCases) || 0,
        })),
      };
      const res = await fetch(`${API_BASE}/${id}`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify(payload) });
      if (!res.ok) throw new Error(`Kaydedilemedi (${res.status})`);
      showToast('success', 'Araç yükleme güncellendi');
      setTimeout(() => navigate('/vehicle-loads'), 400);
    } catch (err) {
      showToast('error', err.message || 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();

  const handleApprove = async () => {
    if (isNew) return;
    setActionBusy(true);
    try {
      const res = await fetch(`${API_BASE}/${id}/approve`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({}),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(payload, 'Message', 'message') || `Onay başarısız (${res.status})`);
      showToast('success', 'Talep onaylandı');
      const data = unwrap(payload) || {};
      const nextStatus = pick(data, 'status', 'Status') || 'APPROVED';
      setForm((c) => ({ ...c, status: nextStatus }));
      setDetailRow((c) => ({ ...(c || {}), status: nextStatus, approvedBy: pick(data, 'approvedBy', 'ApprovedBy'), approvedDate: pick(data, 'approvedDate', 'ApprovedDate'), rejectReason: null }));
    } catch (err) {
      showToast('error', err.message || 'Onay başarısız');
    } finally {
      setActionBusy(false);
    }
  };

  // Red gerekçesi ZORUNLU (spec §6.4) — boş gönderilirse backend 400 döner.
  const submitReject = async () => {
    if (isNew) return;
    const reason = rejectReason.trim();
    if (!reason) { showToast('error', 'Red gerekçesi zorunludur'); return; }
    setActionBusy(true);
    try {
      const res = await fetch(`${API_BASE}/${id}/reject`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ reason }),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(payload, 'Message', 'message') || `Red başarısız (${res.status})`);
      showToast('success', 'Talep reddedildi');
      const data = unwrap(payload) || {};
      // Red artık İPTAL'e düşmez: ayrı REJECTED durumu.
      const nextStatus = pick(data, 'status', 'Status') || 'REJECTED';
      setForm((c) => ({ ...c, status: nextStatus }));
      setDetailRow((c) => ({ ...(c || {}), status: nextStatus, rejectReason: reason }));
      setRejectModal(false);
      setRejectReason('');
    } catch (err) {
      showToast('error', err.message || 'Red başarısız');
    } finally {
      setActionBusy(false);
    }
  };

  const st = form.status;
  const closed = isClosedStatus(st);
  const approvedSt = isApprovedStatus(st);
  const receivedSt = isReceivedStatus(st);
  const plate = form.plate;
  const title = plate ? `Araç Yükleme • ${plate}` : 'Araç Yükleme Detayı';
  const statusBadge = st ? statusBadgeNode(st) : null;

  const approvedBy = pick(detailRow, 'approvedBy', 'ApprovedBy');
  const approvedDate = pick(detailRow, 'approvedDate', 'ApprovedDate');
  const rejectReasonView = pick(detailRow, 'rejectReason', 'RejectReason');

  // Aksiyonlar durum makinesine göre aktifleşir: kapanmış (REJECTED/CANCELLED/RECEIVED*) talepte
  // onay/red yok; zaten onaylanmışta tekrar onay yok.
  const actions = (
    <>
      {!closed && !approvedSt && (
        <button className="dps-btn" style={{ background: '#16a34a', color: '#fff' }} onClick={handleApprove} disabled={saving || actionBusy}>
          <Icon name="check" size={16} /> Onayla
        </button>
      )}
      {!closed && (
        <button className="dps-btn danger" onClick={() => { setRejectReason(''); setRejectModal(true); }} disabled={saving || actionBusy}>
          <Icon name="x" size={16} /> Reddet
        </button>
      )}
      <button className="dps-btn ghost" onClick={() => navigate('/vehicle-loads')} disabled={saving}>Kapat</button>
      {canSave && !closed && !receivedSt && (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
        />
      )}
    </>
  );

  const tabs = [
    { key: 'general', label: 'Genel', icon: 'briefcase' },
    { key: 'lines', label: 'Satırlar', icon: 'box' },
  ];

  return (
    <DetailShell
      crumb={[{ label: 'Araç Yükleme Talepleri', to: '/vehicle-loads' }, { label: 'Araç Yükleme Detayı', current: true }]}
      status={statusBadge}
      tabs={tabs}
      activeTab={activeTab}
      onTab={setActiveTab}
      actions={actions}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'VehicleLoadHeader', entityId: id }}
      loading={loading}
    >
      {activeTab === 'general' && (
        <div className="dps-form">
          <Section icon="briefcase" title="Talep Bilgileri" subtitle="Sevkiyat, araç ve SDM bilgileri" note={title}>
            <Grid cols={2}>
              <FieldText form={form} set={set} k="deliveryDate" label="Sevkiyat Tarihi" placeholder="2026-06-10" />
              <FieldText form={form} set={set} k="plate" label="Plaka" />
              <FieldText form={form} set={set} k="sdmName" label="SDM" />
              <FieldTextarea form={form} set={set} k="notes" label="Notlar" rows={2} span />
            </Grid>
          </Section>

          {(approvedBy || approvedDate || rejectReasonView) && (
            <Section icon="info" title="Onay / Red Bilgisi" subtitle="Talebin onay durumu">
              <Grid cols={2}>
                {(approvedBy || approvedDate) && (
                  <FieldCustom label="Onay Bilgisi">
                    <div className="dps-ff-input"><div style={{ padding: '8px 0' }}>{(approvedBy || '-')}{approvedDate ? ` • ${formatDate(approvedDate)}` : ''}</div></div>
                  </FieldCustom>
                )}
                {rejectReasonView && (
                  <FieldCustom label="Red Sebebi">
                    <div className="dps-ff-input"><div style={{ padding: '8px 0' }}>{rejectReasonView}</div></div>
                  </FieldCustom>
                )}
              </Grid>
            </Section>
          )}
        </div>
      )}

      {activeTab === 'lines' && (
        <div className="dps-form">
          <Section
            icon="box"
            title={`Ürün Satırları (${lines.length})`}
            subtitle="Araca yüklenmesi istenen ürünler ve koli adetleri"
            right={<button className="dps-blk-add" onClick={openAddLine}><Icon name="plus" size={15} /> Satır Ekle</button>}
          >
            {linesLoading ? (
              <div className="dps-empty"><span>Yükleniyor…</span></div>
            ) : lines.length === 0 ? (
              <div className="dps-empty"><Icon name="box" size={34} /><p>Henüz satır yok</p><span>Sağ üstten “Satır Ekle” ile ürün seçin.</span></div>
            ) : (
              <table className="dps-table">
                <thead>
                  <tr><th>Ürün</th><th>Koli</th><th>Pack</th><th>Toplam</th><th className="dps-td-actions">İşlem</th></tr>
                </thead>
                <tbody>
                  {lines.map((line) => {
                    const total = (Number(line.packSize) || 0) * (Number(line.requestedCases) || 0);
                    return (
                      <tr key={line.key}>
                        <td>
                          <strong>{line.productName || '-'}</strong>
                          {line.productCode && <div style={{ fontSize: 11, color: '#6b7280' }}>{line.productCode}</div>}
                        </td>
                        <td>{line.requestedCases ?? '-'}</td>
                        <td>{line.packSize ?? '-'}</td>
                        <td><strong>{total || '-'}</strong></td>
                        <td className="dps-td-actions">
                          <button className="dps-row-btn warn" onClick={() => openEditLine(line)}>Düzenle</button>
                          <button className="dps-row-btn danger" onClick={() => removeLine(line.key)}>Sil</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Section>
        </div>
      )}

      {/* Satır ekle/düzenle modalı */}
      {lineModal && lineForm && (
        <SubModal
          title={lineModal.key ? 'Satırı Düzenle' : 'Yeni Satır'}
          onClose={() => { setLineModal(null); setLineForm(null); }}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => { setLineModal(null); setLineForm(null); }}>İptal</button>
              <button className="dps-btn primary" onClick={confirmLine}>{lineModal.key ? 'Kaydet' : 'Ekle'}</button>
            </>
          )}
        >
          <FieldCustom label="Ürün (kod/ad ara)">
            <RelationPicker
              config={RELATION_CONFIGS.product}
              value={lineForm.productId || ''}
              selectedLabel={lineForm.productName || ''}
              onChange={(val, item) => {
                setLine('productId', val || '');
                setLine('productName', (item && (item.name || item.Name)) || (val ? lineForm.productName : ''));
                setLine('productCode', (item && (item.textId || item.TextId)) || (val ? lineForm.productCode : ''));
              }}
              placeholder="Ürün seç"
            />
          </FieldCustom>
          <Grid cols={2}>
            <FieldText form={lineForm} set={setLine} k="requestedCases" label="Koli" num />
            <FieldText form={lineForm} set={setLine} k="packSize" label="Pack" num />
          </Grid>
        </SubModal>
      )}

      {/* Reddet modalı */}
      {rejectModal && (
        <SubModal
          title="Talebi Reddet"
          maxWidth={460}
          onClose={() => setRejectModal(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setRejectModal(false)} disabled={actionBusy}>Vazgeç</button>
              <button className="dps-btn danger" onClick={submitReject} disabled={actionBusy || !rejectReason.trim()}>{actionBusy ? 'İşleniyor…' : 'Reddet'}</button>
            </>
          )}
        >
          <FieldTextarea
            form={{ rejectReason }}
            set={(_, v) => setRejectReason(v)}
            k="rejectReason"
            label="Red Gerekçesi (zorunlu)"
            rows={3}
          />
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default VehicleLoadDetailPage;
